/**
 * 작성자 : 박승호
 * 날짜 : 2024.10.11
 * 기능 : 텍스트 안내 박스
 */

import DOMPurify from 'dompurify';
import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Root = styled.div`
  position: sticky;
  left: 0;
  right: 0;
  user-select: none;
  display: flex;
  align-items: flex-start;
  font-size: 0.875rem;
  width: 100%;
  padding: 0.2rem 1rem;
  border-radius: 0.2rem;
  background-color: var(--outline-secondary);
  color: var(--text-quaternary);
  gap: 0.5rem;

  &.red {
    background-color: var(--tonal-red);
    color: var(--filled-red);
  }

  &.blue {
    background-color: var(--tonal-blue);
    color: var(--primary);
  }

  &.orange {
    background-color: var(--tonal-orange);
    color: rgb(217, 119, 6);

    .material-symbols-rounded {
      color: #f59e0b;
    }
  }

  &.stroke {
    background-color: transparent;
    border: 1px solid var(--outline);

    &.red {
      border-color: var(--filled-red);
    }

    &.blue {
      border-color: var(--primary);
    }

    &.orange {
      border-color: var(--filled-amber-deep);
    }
  }

  .material-symbols-rounded {
    font-variation-settings: 'FILL' 1, 'wght' 300, 'GRAD' 0, 'opsz' 24;
    padding-top: 0.2rem;
  }

  &.info {
    .material-symbols-rounded {
      font-variation-settings: 'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24;
    }
  }

  .text {
    padding: 0.2rem 0;
  }
`;

interface Props {
  ico?: string; // icon
  description: string; // 설명
  color?: 'normal' | 'red' | 'blue' | 'orange'; // 색상
  type?: 'fill' | 'stroke'; // 텍스트 박스 타입
  style?: CSSProperties; // 커스텀 css
}

const TextInfoBox = ({ ico, description, color = 'normal', type = 'fill', style }: Props) => {
  const { t } = useTranslation();
  return (
    <Root className={`${color} ${type} ${ico && ico}`} style={style}>
      {ico && <span className='material-symbols-rounded'>{ico}</span>}
      <span className='text' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t(description)) }} />
    </Root>
  );
};

export default TextInfoBox;
